// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/ui/izgara/izgara-plan.ts
// Fark: ham metin yerine genel `GridColumn<R>` (getText/setText) üstünde çalışır; plan doğrudan yeni
// satır dizisini ve yazılan hücreleri döndürür; yapıştırma `newRow` ile satır büyütür.
import { parseTsv, serializeTsv } from './tsv'
import type { Coord, GridColumn, Range } from './types'

export interface WritePlan<R> {
  rows: R[]
  /** Cells that were written (in the new `rows`). */
  written: Coord[]
  /** Bounding rectangle of what was touched (for selection after undo/paste). */
  area: Range
}

/** May this cell be edited? Readonly columns, readonly rows and a readonly grid never are. */
export function isEditable<R>(col: GridColumn<R>, row: R, gridReadOnly = false): boolean {
  return !gridReadOnly && col.kind !== 'readonly' && !col.readOnly?.(row)
}

/** Display text of a cell for copying: select columns copy the option label. */
export function cellText<R>(col: GridColumn<R>, row: R): string {
  const text = col.getText(row)
  if (col.kind !== 'select' || text === '') return text
  return col.options?.(row).find((o) => o.value === text)?.label ?? text
}

/**
 * Pasted/typed text -> value for `setText`. Select columns map an option label (then a value)
 * case-insensitively to the option value; unmatched text is passed as-is.
 */
export function resolveInput<R>(col: GridColumn<R>, row: R, text: string): string {
  if (col.kind !== 'select' || text === '') return text
  const options = col.options?.(row) ?? []
  const needle = text.trim().toLocaleLowerCase('tr')
  const byLabel = options.find((o) => o.label.toLocaleLowerCase('tr') === needle)
  if (byLabel) return byLabel.value
  const byValue = options.find((o) => o.value.toLocaleLowerCase('tr') === needle)
  return byValue ? byValue.value : text
}

function bounds(written: Coord[]): Range | null {
  if (written.length === 0) return null
  let r0 = Infinity
  let r1 = -Infinity
  let c0 = Infinity
  let c1 = -Infinity
  for (const w of written) {
    r0 = Math.min(r0, w.row)
    r1 = Math.max(r1, w.row)
    c0 = Math.min(c0, w.col)
    c1 = Math.max(c1, w.col)
  }
  return { r0, r1, c0, c1 }
}

function finish<R>(rows: R[], written: Coord[]): WritePlan<R> | null {
  const area = bounds(written)
  return area ? { rows, written, area } : null
}

/** TSV text of a selection rectangle. */
export function copyRange<R>(rows: R[], columns: GridColumn<R>[], range: Range): string {
  const matrix: string[][] = []
  for (let r = range.r0; r <= range.r1; r++) {
    const row = rows[r]
    if (row === undefined) continue
    const line: string[] = []
    for (let c = range.c0; c <= range.c1; c++) {
      const col = columns[c]
      line.push(col ? cellText(col, row) : '')
    }
    matrix.push(line)
  }
  return serializeTsv(matrix)
}

interface PasteArgs<R> {
  rows: R[]
  columns: GridColumn<R>[]
  /** Clipboard text (TSV). */
  text: string
  /** Current selection; the paste starts at its top-left. */
  range: Range
  newRow?: () => R
  readOnly?: boolean
}

/**
 * Plans a paste. Starts at the range's top-left, grows rows with `newRow` (absent = rows past the
 * end are dropped), skips readonly cells, drops columns past the last. A single pasted value into a
 * multi-cell range fills the whole range. Returns null when nothing would be written.
 */
export function planPaste<R>({
  rows,
  columns,
  text,
  range,
  newRow,
  readOnly,
}: PasteArgs<R>): WritePlan<R> | null {
  if (readOnly) return null
  const block = parseTsv(text)
  if (block.length === 0) return null
  const next = rows.slice()
  const written: Coord[] = []

  const write = (r: number, c: number, value: string) => {
    const col = columns[c]
    if (!col) return
    if (r >= next.length) {
      if (!newRow) return
      while (next.length <= r) next.push(newRow())
    }
    const row = next[r]!
    if (!isEditable(col, row)) return
    next[r] = col.setText(row, resolveInput(col, row, value))
    written.push({ row: r, col: c })
  }

  const single = block.length === 1 && block[0]!.length === 1
  if (single && (range.r1 > range.r0 || range.c1 > range.c0)) {
    for (let r = range.r0; r <= range.r1; r++) {
      for (let c = range.c0; c <= range.c1; c++) write(r, c, block[0]![0]!)
    }
  } else {
    block.forEach((line, i) => line.forEach((value, j) => write(range.r0 + i, range.c0 + j, value)))
  }
  return finish(next, written)
}

/** Ctrl+D: the first row of the range is copied down through the range, per column. */
export function planFillDown<R>(
  rows: R[],
  columns: GridColumn<R>[],
  range: Range,
  readOnly?: boolean,
): WritePlan<R> | null {
  if (readOnly || range.r1 <= range.r0) return null
  const source = rows[range.r0]
  if (source === undefined) return null
  const next = rows.slice()
  const written: Coord[] = []
  for (let c = range.c0; c <= range.c1; c++) {
    const col = columns[c]
    if (!col) continue
    const text = col.getText(source)
    for (let r = range.r0 + 1; r <= range.r1; r++) {
      const row = next[r]
      if (row === undefined || !isEditable(col, row)) continue
      next[r] = col.setText(row, text)
      written.push({ row: r, col: c })
    }
  }
  return finish(next, written)
}

/** Delete / Backspace / Ctrl+X: every editable cell of the range becomes ''. */
export function planClear<R>(
  rows: R[],
  columns: GridColumn<R>[],
  range: Range,
  readOnly?: boolean,
): WritePlan<R> | null {
  if (readOnly) return null
  const next = rows.slice()
  const written: Coord[] = []
  for (let r = range.r0; r <= range.r1; r++) {
    for (let c = range.c0; c <= range.c1; c++) {
      const col = columns[c]
      const row = next[r]
      if (!col || row === undefined || !isEditable(col, row)) continue
      next[r] = col.setText(row, '')
      written.push({ row: r, col: c })
    }
  }
  return finish(next, written)
}

/** Selection rectangle of a plan's area (used after paste). */
export function areaSelection(area: Range): { anchor: Coord; extent: Coord } {
  return {
    anchor: { row: area.r0, col: area.c0 },
    extent: { row: area.r1, col: area.c1 },
  }
}
